import { useState, useEffect } from 'react';
import type { GuideQuestion as GuideQuestionType } from '@/data/types';

interface GuideQuestionProps {
  question: GuideQuestionType;
  questionIndex: number;
  totalQuestions: number;
  onAnswer: (selectedIds: string[]) => void;
  savedSelectedIds?: string[];
}

export default function GuideQuestion({
  question,
  questionIndex,
  totalQuestions,
  onAnswer,
  savedSelectedIds,
}: GuideQuestionProps) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    if (savedSelectedIds && savedSelectedIds.length > 0) {
      setSelectedIds(savedSelectedIds);
      onAnswer(savedSelectedIds);
    } else {
      setSelectedIds([]);
    }
  }, [question.id]);

  const handleSelect = (optionId: string) => {
    if (question.selectionType === 'single') {
      setSelectedIds([optionId]);
      onAnswer([optionId]);
      return;
    }

    const option = question.options.find((o) => o.id === optionId);
    if (option?.exclusive) {
      setSelectedIds([optionId]);
      onAnswer([optionId]);
      return;
    }

    const exclusiveSelected = selectedIds.find((id) => {
      const opt = question.options.find((o) => o.id === id);
      return opt?.exclusive;
    });

    if (exclusiveSelected) {
      setSelectedIds([optionId]);
      onAnswer([optionId]);
      return;
    }

    const nextIds = selectedIds.includes(optionId)
      ? selectedIds.filter((id) => id !== optionId)
      : [...selectedIds, optionId];

    setSelectedIds(nextIds);
    onAnswer(nextIds);
  };

  const isSelected = (optionId: string) => selectedIds.includes(optionId);

  return (
    <div className="flex flex-col">
      <div className="mb-4">
        <h2 className="text-[20px] text-label-normal mb-2 leading-[1.45] font-bold">
          {question.questionText}
        </h2>
        {question.helperText && (
          <p className="text-[13px] text-label-alt leading-[1.5]">{question.helperText}</p>
        )}
      </div>

      <div className="space-y-2">
        {question.options.map((option) => {
          const selected = isSelected(option.id);
          const hasExpandedContent = selected && (option.reaction || question.tipText);

          return (
            <button
              key={option.id}
              onClick={() => handleSelect(option.id)}
              className={`w-full text-left rounded-[14px] border transition-all cursor-pointer ${
                selected
                  ? 'border-primary-normal bg-static-white'
                  : 'border-line-divider bg-static-white hover:border-line-strong'
              }`}
            >
              <div className="px-[14px] py-3">
                <div className="flex items-start justify-between gap-2.5">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-[15px] text-label-normal font-bold">{option.label}</span>
                      {option.exclusive && (
                        <span className="inline-block px-2 py-0.5 rounded-full bg-fill-neutral-chip text-label-assistive text-caption">
                          잘 모르겠어요
                        </span>
                      )}
                    </div>
                    {option.description && (
                      <p className="text-[13px] text-label-alt leading-[1.45]">{option.description}</p>
                    )}
                  </div>
                  <div className={`w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                    selected
                      ? 'border-primary-normal bg-primary-normal'
                      : 'border-line-strong'
                  }`}>
                    {selected && (
                      <i className="ri-check-line text-static-white text-[10px]"></i>
                    )}
                  </div>
                </div>
              </div>

              {hasExpandedContent && (
                <div className="px-[14px] pb-3">
                  <div className="w-full h-px bg-line-divider mb-2.5"></div>
                  {option.reaction && (
                    <p className="text-[13px] text-label-neutral leading-[1.55] mb-2">
                      {option.reaction}
                    </p>
                  )}
                  {question.tipText && (
                    <div className="flex items-start gap-1.5 p-2 rounded-lg bg-fill-alt">
                      <div className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-lightbulb-line text-accent-gold text-[11px]"></i>
                      </div>
                      <p className="text-[12px] text-label-alt leading-[1.5]">
                        <span className="font-semibold text-label-neutral">플래너 팁 </span>
                        {question.tipText}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}